import { useState, useMemo, useEffect } from 'react';
import { Navigate, useParams, Link } from 'react-router-dom';

import { useAuth, useCourses, useModal, useToast } from 'contexts';
import { Placeholder } from 'components';
import { getCourseStatus, getResumeLesson } from 'utils';
import Tree from './Tree';
import { ProgressInterface } from 'types';

import { getCertificado, getCompletedCourses, getFinishCourse } from 'services';
import { GrCertificate } from 'react-icons/gr';
import { ModalPrintCertificate } from 'components/ModalPrintCertificate';
import { ModalCourseResources } from 'components/ModalCourseResources';

import './style.scss';

import noImage from 'assets/images/no-image.jpg';

export default function CoursesDetailPage() {
  const { user, profileUser } = useAuth();
  const { allProgress, listCourses } = useCourses();
  const { courseID } = useParams();
  const { showModal, closeModal } = useModal();
  const [showPrint, setShowPrint] = useState<boolean>(false);

  const [opened, setOpened] = useState<any>();
  const [printCertifield, setHasPrintCertifield] = useState(false);
  const [certificado, setCertificado] = useState(false);

  const course = useMemo(() => {
    if (Array.isArray(listCourses) && listCourses.length) {
      const _course = listCourses.find(x => x._id === courseID);

      if (!!_course) {
        return _course;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }, [courseID, listCourses]);

  useEffect(() => {
    if (user) {
      checkCompletedCourses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function checkCompletedCourses() {
    if (!user) return;
    const response = await getCompletedCourses({}, 'items=0-1000');

    const hasCourseCompleted = response?.data?.some((courseComplete: any) => courseID === courseComplete.attributes.course);
    setShowPrint(hasCourseCompleted);
  }

  async function printCertified() {
    const certificado = await getCertificado(courseID);
    showModal(
      'text',
      <ModalPrintCertificate certifield={certificado} profiles={profileUser?.roles} courseID={courseID} />,
      'text-black',
    );
  }

  function openDidacticResources() {
    showModal('resources-didaticos', <ModalCourseResources courseID={courseID} courseTitle={course?.title} />, 'text-black');
  }

  const courseProgress = useMemo(() => {
    if (Array.isArray(allProgress) && allProgress.length) {
      const _course = allProgress.find(x => x._id === courseID);

      if (!!_course) {
        return _course;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }, [allProgress, courseID]);

  const resumeLink = useMemo(() => {
    if (courseProgress && courseProgress.percent > 0 && courseProgress.percent < 100) {
      const resume = getResumeLesson(courseProgress);
      if (resume) return `/cursos/${courseID}/${resume.folderID}/${resume.contentID}`;
    }
    return null;
  }, [courseProgress, courseID]);

  useEffect(() => {
    if (user && courseID) {
      hasPrintCertificate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, courseID]);

  async function hasPrintCertificate() {
    if (!user || !courseID) return;
    const data = await getFinishCourse(user._id, courseID);
    const certificado = await getCertificado(courseID);

    if (data.length > 0 && certificado.length > 0) {
      setHasPrintCertifield(true);
    }

    if (certificado.length > 0) {
      setCertificado(true);
    }
  }

  if (!Array.isArray(listCourses)) {
    return (
      <>
        <div className="flex flex-col space-y-6">
          <div className="flex flex-row space-x-3">
            <Placeholder className="w-10 h-10 rounded-full" />
            <Placeholder className="flex-1 h-10 rounded-full" />
          </div>

          <Placeholder className="h-64" />

          <div className="grid grid-cols-3 h-48 gap-6">
            <Placeholder />
            <Placeholder />
            <Placeholder />
          </div>
        </div>
      </>
    );
  }

  if (!course) {
    return <Navigate to={`/erro-404`} replace />;
  }

  return (
    <>
      <section className="w-full">
        {/* Course Info Header */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-5 mb-8">
          <div className="w-full lg:w-[200px] h-[180px] lg:h-[200px] rounded-2xl overflow-hidden flex-shrink-0 relative bg-gray-100">
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-transparent z-[1]" />
            <img
              src={course.extra?.image?.length ? course.extra?.image : noImage}
              alt={course.title || ' '}
              className="w-full h-full block object-cover"
            />
          </div>

          <div className="flex-1">
            {!!course.title && <h2 className="text-xl lg:text-3xl font-bold text-[#181b1a] mb-1">{course.title}</h2>}

            {!!getCourseStatus(courseProgress?.percent).length && (
              <p className="text-sm lg:text-base font-bold uppercase text-[#2bb788] mb-0.5">
                {getCourseStatus(courseProgress?.percent)}
              </p>
            )}

            {typeof courseProgress?.percent === 'number' && courseProgress?.percent > 0 && (
              <p className="text-sm lg:text-base font-bold text-[#181b1a]">
                {`${Math.round(courseProgress?.percent)}% concluído`}
              </p>
            )}

            {!!course.extra?.description && (
              <div
                className="text-sm text-gray-500 mt-3 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: course.extra.description }}
              />
            )}

            <div className="flex items-center gap-3 flex-wrap mt-3">
              {!!resumeLink && (
                <Link
                  to={resumeLink}
                  className="flex items-center gap-2 bg-orange text-white rounded-full py-2.5 px-5 text-sm font-medium transition-opacity hover:opacity-90"
                >
                  Continuar
                  <span className="text-base">→</span>
                </Link>
              )}

              {!!certificado && (
                <div className="relative group inline-block">
                  <button
                    className="flex items-center bg-[#503e9d20] text-[#503e9d] rounded-full py-2.5 px-5
                      disabled:bg-gray-200 disabled:opacity-40 disabled:text-gray-500 text-sm font-medium transition-colors"
                    onClick={printCertified}
                    disabled={!printCertifield}
                  >
                    <GrCertificate className="mr-2" /> Ver certificado
                  </button>

                  {!printCertifield && (
                    <span
                      className="absolute top-12 left-1/2 -translate-x-1/2
                      whitespace-nowrap bg-black text-white text-xs rounded px-3 py-1
                      opacity-0 group-hover:opacity-100 transition pointer-events-none"
                    >
                      Conclua o curso para liberar o certificado.
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="relative group inline-block mt-3">
              <button
                type="button"
                onClick={openDidacticResources}
                disabled={!courseID}
                className="flex items-center bg-[#503e9d20] text-[#503e9d] rounded-full py-2.5 px-5
                  disabled:bg-gray-200 disabled:opacity-40 disabled:text-gray-500 text-sm font-medium transition-colors"
              >
                Recursos didáticos
              </button>
            </div>
          </div>
        </div>

        {/* Trails / Content */}
        {!!courseProgress && !!courseID && (
          <div className="pt-6 mt-6 border-t border-gray-100 w-full">
            <h3 className="text-lg lg:text-xl font-medium text-[#181b1a] mb-5">Conteúdo programático</h3>
            <Tree items={courseProgress?.items as ProgressInterface[]} parent={courseProgress._id} courseID={courseID} />
          </div>
        )}
      </section>
    </>
  );
}
